// @flow
import URI from 'urijs';
import lz from 'lz-string';
import cookie from 'js-cookie';
import { get } from 'lodash';
import { oidc as oidcUtils, windowWrapper } from '@clubmed/utils';

import * as routeBuilder from 'Routes/routeBuilder';
import * as pleUtils from 'Utils/ple-utils';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import { PAYMENT_INIT, PAYMENT_PRE_GET_FORM_DATA } from 'Utils/request/requestDictionary';
import { getInitPaymentQuery, getFormDataQuery, type InitPayload } from './PrePayment.queries';

function initPayment(payload: InitPayload, appContext: *) {
  return graphqlClient
    .runNoCacheQuery(getInitPaymentQuery(payload), appContext, {
      requestName: PAYMENT_INIT,
    })
    .then(({ prePayment }) => prePayment || {});
}

function buildPrePaymentUrl(payload: * = {}, locale) {
  const t = payload ? lz.compressToEncodedURIComponent(JSON.stringify(payload)) : '';
  const query = {
    t,
    locale,
  };
  return new URI(routeBuilder.urlPattern('prePayment')).query(query).toString();
}

export function savePaymentTrackingDatas(orderId: string, values: *) {
  cookie.set('PRE_PAYMENT_INFOS-' + orderId, oidcUtils.toUriEncodedState(values));
}

export function getPaymentTrackingDatas(orderId: string) {
  const value = cookie.get(`PRE_PAYMENT_INFOS-${orderId}`);
  return value ? oidcUtils.fromUriEncodedState(value) : null;
}

export function makeBuildPrePaymentUrl(appContext: *) {
  return function createPrePaymentUrl(params: InitPayload = {}) {
    return initPayment(params, appContext).then(datas => {
      const payload = {
        ...params,
        ...datas,
      };
      if (params.urlRedirectionIfNothingToPay && datas.amount === 0) {
        if (params.beforeRedirect) {
          params.beforeRedirect();
        }
        return params.urlRedirectionIfNothingToPay;
      }
      return buildPrePaymentUrl(payload, get(appContext, 'locale'));
    });
  };
}

type BuildPrePaymentUrlAndRedirectParams = {|
  appContext: AppContext,
  location: RouterLocation<*, *>,
  payload?: InitPayload,
  history: RouterHistory,
  isFreeDeposit?: boolean,
|};
export function buildPrePaymentUrlAndRedirect({
  appContext,
  history,
  location,
  payload = {},
  isFreeDeposit = false,
}: BuildPrePaymentUrlAndRedirectParams) {
  return makeBuildPrePaymentUrl(appContext)(payload)
    .then(url => {
      history.pushRoute(isFreeDeposit ? new URI(url).addQuery('freedeposit').toString() : url);
    })
    .catch(err => {
      if (pleUtils.hasAuthResponseError(err)) {
        pleUtils.redirectOnLoginPage({
          locale: appContext.locale,
          redirectPath: location.pathname,
        });
        return;
      }
      if (pleUtils.hasNotFoundError(err)) {
        windowWrapper.setLocation('/404');
        return;
      }
      throw err;
    });
}

export function getFormData(params: *, appContext: AppContext) {
  return graphqlClient.runNoCacheQuery(getFormDataQuery(params), appContext, {
    requestName: PAYMENT_PRE_GET_FORM_DATA,
  });
}

export function extractPayload(payload: *) {
  if (!payload) {
    return null;
  }
  let value = null;
  try {
    value = lz.decompressFromEncodedURIComponent(payload);
    return JSON.parse(value);
  } catch (e) {
    console.error('extractPayload', e, ' for value:', value); // eslint-disable-line no-console
    return null;
  }
}

export function getDevice(responsive: ResponsiveProps) {
  if (responsive.isDesktop) return 'DESKTOP';
  if (responsive.isTablet) return 'TABLET';
  return 'MOBILE';
}
